import { Box, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AnimatePresence, motion, PanInfo } from "framer-motion";
import React, { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import useWindowSize from "../../../hooks/useWindowSize";
import { getOptimizedImageUrl, BREAKPOINT, trackClick } from "../../_helpers";
import { isMobile } from "react-device-detect";
import { galleryFill } from "../../../utils/galleryGrid";

const InstagramIcon = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24 4.32C30.41 4.32 31.17 4.35 33.69 4.46C36.02 4.56 37.31 4.95 38.16 5.28C39.29 5.71 40.11 6.23 40.95 7.07C41.8 7.92 42.31 8.73 42.74 9.86C43.07 10.71 43.46 12.01 43.56 14.33C43.67 16.85 43.7 17.61 43.7 24.02C43.7 30.43 43.67 31.19 43.56 33.71C43.46 36.03 43.07 37.33 42.74 38.18C42.31 39.31 41.79 40.13 40.95 40.97C40.1 41.82 39.29 42.33 38.16 42.76C37.31 43.09 36.01 43.48 33.69 43.58C31.17 43.69 30.41 43.72 24 43.72C17.59 43.72 16.83 43.69 14.31 43.58C11.99 43.48 10.69 43.09 9.84 42.76C8.71 42.33 7.89 41.81 7.05 40.97C6.2 40.12 5.69 39.31 5.26 38.18C4.93 37.33 4.54 36.03 4.44 33.71C4.33 31.19 4.3 30.43 4.3 24.02C4.3 17.61 4.33 16.85 4.44 14.33C4.54 12.01 4.93 10.71 5.26 9.86C5.69 8.73 6.21 7.91 7.05 7.07C7.9 6.22 8.71 5.71 9.84 5.28C10.69 4.95 11.99 4.56 14.31 4.46C16.83 4.35 17.59 4.32 24 4.32ZM24 0C17.48 0 16.65 0.03 14.1 0.14C11.57 0.25 9.8 0.66 8.27 1.26C6.68 1.88 5.33 2.71 4 4.05C2.66 5.39 1.83 6.74 1.21 8.33C0.61 9.86 0.2 11.63 0.09 14.16C-0.02 16.71 -0.05 17.54 -0.05 24.06C-0.05 30.58 -0.02 31.41 0.09 33.96C0.2 36.49 0.61 38.26 1.21 39.79C1.83 41.38 2.66 42.73 4 44.07C5.34 45.41 6.69 46.24 8.28 46.86C9.81 47.46 11.58 47.87 14.11 47.98C16.66 48.09 17.49 48.12 24.01 48.12C30.53 48.12 31.36 48.09 33.91 47.98C36.44 47.87 38.21 47.46 39.74 46.86C41.33 46.24 42.68 45.41 44.02 44.07C45.36 42.73 46.19 41.38 46.81 39.79C47.41 38.26 47.82 36.49 47.93 33.96C48.04 31.41 48.07 30.58 48.07 24.06C48.07 17.54 48.04 16.71 47.93 14.16C47.82 11.63 47.41 9.86 46.81 8.33C46.19 6.74 45.36 5.39 44.02 4.05C42.68 2.71 41.33 1.88 39.74 1.26C38.21 0.66 36.44 0.25 33.91 0.14C31.35 0.03 30.52 0 24 0Z"
      fill="var(--logoColor2)"
    />
    <path
      d="M24 11.67C17.19 11.67 11.67 17.19 11.67 24C11.67 30.81 17.19 36.33 24 36.33C30.81 36.33 36.33 30.81 36.33 24C36.33 17.19 30.81 11.67 24 11.67ZM24 32C19.6 32 16 28.4 16 24C16 19.6 19.6 16 24 16C28.4 16 32 19.6 32 24C32 28.4 28.4 32 24 32Z"
      fill="var(--logoColor2)"
    />
    <path
      d="M39.69 11.18C39.69 12.78 38.38 14.08 36.79 14.08C35.19 14.08 33.89 12.77 33.89 11.18C33.89 9.58999 35.2 8.27999 36.79 8.27999C38.38 8.27999 39.69 9.58999 39.69 11.18Z"
      fill="var(--logoColor2)"
    />
  </svg>
);

interface GallerySectionProps {
  artistData: {
    name: string;
    images?: Array<{ url: string }>;
    topTracks?: Array<{ album?: { images?: Array<{ url: string }> } }>;
  };
  compact?: boolean;
}

const TRUEVINED_GALLERY_IMAGES = [
  { url: "/gallery/optimized/truevined1b.webp" },
  { url: "/gallery/optimized/truevined2.webp" },
  { url: "/gallery/optimized/truevined3.webp" },
];

export function GallerySection(props: GallerySectionProps) {
  const { artistData, compact = false } = props;
  const windowSize = useWindowSize();
  const reallyIsMobile = isMobile || windowSize.width <= BREAKPOINT;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const theme = useTheme();

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  if (!artistData) {
    return null;
  }

  const isTruevined = artistData.name?.toLowerCase() === "truevined";
  const artistImages = artistData.images || [];
  const topTracks = artistData.topTracks || [];

  const getImageBaseId = (url: string) => {
    if (!url) return null;
    const match = url.match(/\/image\/([a-zA-Z0-9]+)/);
    return match ? match[1] : url;
  };

  const uniqueImages: Array<{ url: string }> = [];
  const seenImageIds = new Set<string>();

  if (isTruevined) {
    TRUEVINED_GALLERY_IMAGES.forEach((image) => {
      const baseId = getImageBaseId(image.url);
      if (baseId && !seenImageIds.has(baseId)) {
        seenImageIds.add(baseId);
        uniqueImages.push(image);
      }
    });
  }

  if (artistImages.length > 0 && artistImages[0]?.url) {
    const baseId = getImageBaseId(artistImages[0].url);
    if (baseId && !seenImageIds.has(baseId)) {
      seenImageIds.add(baseId);
      uniqueImages.push(artistImages[0]);
    }
  }

  topTracks.forEach((track) => {
    const albumImages = track?.album?.images || [];
    const largestImage = albumImages[0];
    if (largestImage?.url) {
      const baseId = getImageBaseId(largestImage.url);
      if (baseId && !seenImageIds.has(baseId)) {
        seenImageIds.add(baseId);
        uniqueImages.push(largestImage);
      }
    }
  });

  const galleryImages = uniqueImages.slice(0, isTruevined ? 12 : 8);
  const fillSx = (columns: number) => {
    const fill = galleryFill(galleryImages.length, columns);
    return {
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      "& > :first-of-type": { gridColumn: fill.leadSpan === 2 ? "span 2" : "auto" },
      "& > :first-of-type img": { objectPosition: "center 30%" },
      [`& > :nth-of-type(n+${fill.visible + 1})`]: { display: "none" },
    };
  };
  const galleryGridSx = {
    [theme.breakpoints.only("xs")]: { ...fillSx(3), gap: "0.75rem" },
    [theme.breakpoints.only("sm")]: { ...fillSx(2), gap: "0.75rem" },
    [theme.breakpoints.up("md")]: { ...fillSx(compact ? 3 : 4), gap: "1.25rem" },
  };

  const paginate = useCallback((newDirection: number) => {
    const newIndex = selectedImageIndex + newDirection;
    if (newIndex >= 0 && newIndex < galleryImages.length) {
      setDirection(newDirection);
      setSelectedImageIndex(newIndex);
    }
  }, [selectedImageIndex, galleryImages.length]);

  const handleDragEnd = useCallback((e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipe = swipePower(info.offset.x, info.velocity.x);
    if (swipe < -swipeConfidenceThreshold) {
      paginate(1);
    } else if (swipe > swipeConfidenceThreshold) {
      paginate(-1);
    }
  }, [paginate]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      paginate(-1);
    } else if (e.key === "ArrowRight") {
      paginate(1);
    } else if (e.key === "Escape") {
      setModalOpen(false);
    }
  }, [paginate]);

  React.useEffect(() => {
    if (modalOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [modalOpen, handleKeyDown]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  return (
    <Box
      className="GallerySection"
      sx={{
        position: "relative",
        width: compact ? "100%" : `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: compact ? { xs: "2rem 0", md: "2rem 0" } : { xs: "4rem 2rem", md: "3.5rem" },
        m: compact ? 0 : { xs: "1rem 2.5rem 0", md: "12.25rem 2.5rem 0" },
        backgroundColor: compact ? "transparent" : "var(--color5)",
        borderRadius: compact ? 0 : "1.875rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: { xs: "2.5rem", md: "2.5rem" },
        zIndex: 1,
      }}
    >
      <Box
        className="GalleryHeader"
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: "2rem", md: "2.5rem" },
          flexWrap: "wrap",
          position: "relative",
        }}
      >
        <Typography
          className="Gallery"
          sx={{
            color: "var(--logoColor2)",
            fontSize: { xs: "3rem", md: compact ? "2rem" : "3.34rem" },
            fontFamily: "Hanson",
            fontWeight: "700",
          }}
        >
          GALLERY
        </Typography>
        <Box
          component="a"
          href={`https://instagram.com/${artistData.name.toLowerCase() === "truevined" ? "truevined" : artistData.name.toLowerCase().replace(/\s+/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="InstagramLogoAndUsername"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "0.75rem", md: "0.75rem" },
            textDecoration: "none",
            transition: "opacity 0.2s ease",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        >
          <InstagramIcon size={compact ? 24 : 28} />
          <Typography
            sx={{
              color: "var(--logoColor2)",
              fontSize: { xs: "1.5rem", md: compact ? "1rem" : "1.25rem" },
              fontFamily: "Poppins",
              fontWeight: "700",
            }}
          >
            @{artistData.name.toLowerCase() === "truevined" ? "truevined" : artistData.name.toLowerCase().replace(/\s+/g, '')}
          </Typography>
        </Box>
      </Box>

      {galleryImages.length > 0 ? (
        <>
          <Box
            className="ImageGallery"
            sx={{
              width: "100%",
              display: "grid",
              overflow: "hidden",
              ...galleryGridSx,
            }}
          >
            {galleryImages.map((image, index) => (
              <motion.div
                key={`gallery-${index}`}
                layoutId={`gallery-image-${index}`}
                onClick={() => {
                  trackClick("gallery-image", { label: `Gallery image ${index + 1}`, section: "gallery", artist: artistData?.name, value: index + 1 });
                  setSelectedImageIndex(index);
                  setDirection(0);
                  setModalOpen(true);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                style={{ cursor: "pointer" }}
              >
                <Box
                  component="img"
                  src={image.url.startsWith("/") ? image.url : getOptimizedImageUrl(image.url, { width: 480, height: 480, fit: "cover", quality: 80 })}
                  alt={`${artistData.name} - Image ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: "100%",
                    height: { xs: "10rem", sm: "18rem", md: compact ? "12rem" : "15rem" },
                    objectFit: "cover",
                    borderRadius: { xs: "0.65rem", md: "0.73rem" },
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  }}
                />
              </motion.div>
            ))}
          </Box>

        </>
      ) : null}

      {typeof window !== "undefined" && modalOpen && createPortal(
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setModalOpen(false);
              }}
              aria-label="Close gallery"
              sx={{
                position: "absolute",
                top: { xs: 20, md: 30 },
                right: { xs: 20, md: 30 },
                color: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
                zIndex: 10002,
                padding: { xs: "1.5rem", md: "1rem" },
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.2)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: "3rem", md: "2rem" } }} />
            </IconButton>

            {selectedImageIndex > 0 && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  paginate(-1);
                }}
                aria-label="Previous image"
                sx={{
                  position: "absolute",
                  left: { xs: 10, md: 30 },
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  zIndex: 10002,
                  padding: { xs: "1.5rem", md: "1rem" },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: { xs: "3rem", md: "2.5rem" } }} />
              </IconButton>
            )}

            {selectedImageIndex < galleryImages.length - 1 && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  paginate(1);
                }}
                aria-label="Next image"
                sx={{
                  position: "absolute",
                  right: { xs: 10, md: 30 },
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  zIndex: 10002,
                  padding: { xs: "1.5rem", md: "1rem" },
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: { xs: "3rem", md: "2.5rem" } }} />
              </IconButton>
            )}

            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: "relative",
                width: { xs: "90vw", md: "70vw" },
                height: { xs: "70vh", md: "80vh" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.img
                  key={selectedImageIndex}
                  src={galleryImages[selectedImageIndex]?.url?.startsWith("/")
                    ? galleryImages[selectedImageIndex]?.url
                    : getOptimizedImageUrl(galleryImages[selectedImageIndex]?.url, { width: 1200, quality: 90 })}
                  alt={`${artistData.name} - Image ${selectedImageIndex + 1}`}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                  }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={1}
                  onDragEnd={handleDragEnd}
                  style={{
                    position: "absolute",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                    cursor: "grab",
                    touchAction: "pan-y",
                  }}
                />
              </AnimatePresence>
            </Box>

            <Box
              sx={{
                position: "absolute",
                bottom: { xs: 20, md: 30 },
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: { xs: "1rem", md: "0.5rem" },
                zIndex: 10002,
              }}
            >
              {galleryImages.map((_, index) => (
                <Box
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDirection(index > selectedImageIndex ? 1 : -1);
                    setSelectedImageIndex(index);
                  }}
                  sx={{
                    width: { xs: "1rem", md: "0.75rem" },
                    height: { xs: "1rem", md: "0.75rem" },
                    borderRadius: "50%",
                    backgroundColor: index === selectedImageIndex ? "var(--logoColor2)" : "rgba(255,255,255,0.4)",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "var(--logoColor2)",
                    },
                  }}
                />
              ))}
            </Box>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {galleryImages.length === 0 && (
        <Box
          sx={{
            width: "100%",
            padding: { xs: "4rem 2rem", md: "5rem 3rem" },
            textAlign: "center",
            backgroundColor: "var(--color4)",
            borderRadius: { xs: "1rem", md: "1rem" },
          }}
        >
          <Typography
            sx={{
              color: "var(--logoColor2)",
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              fontFamily: "Poppins",
              fontWeight: "400",
            }}
          >
            Gallery coming soon. Follow {artistData.name} on social media for the latest updates and exclusive content.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
